/**
 * Tab "Local Sales" — datasheet bám `form_frmCtrlSalesQryLocalS.txt` (45 cột)
 * + 3 nút toolbar `frmCtrlSalesMainSubFormLocalS` (Forced close / Re-open / Filling Docs).
 *
 * Style copy `SaleContractPage > ContractListView`: filter buttons + popovers,
 * virtuoso table, total row, TablePagination defaults to "All".
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { type CSSProperties, type MouseEvent, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  DataframeBody,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  type ColumnFilterAnchorPosition,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useIsMobile } from '../../hooks/useResponsive'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  useGetCtrlSalesLocalSQuery,
  useReopenLocalSMutation,
  useToggleFillingDocsLocalSMutation,
  type SaleControlFinalizedFilter,
} from '../../store/api/saleControlApi'
import { getErrorMessage } from '../../utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  toggleTableSort,
  type TableSortState,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { HIDE_ON_TABLET_INLINE } from '../../theme/responsive'

import SaleControlLocalSCloseDialog from './SaleControlLocalSCloseDialog'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { formatCell, isYesText, type SaleControlColumnDef } from './saleControlAccessUi'

type FilterKind = 'search'

export const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

/**
 * Cột + thứ tự bám `ColumnOrder` của Access `frmCtrlSalesQryLocalS`
 * (nguồn chuẩn `frontend/plan/form_web_ui_structure.csv`, KHÔNG dùng thứ tự
 * khai báo `Begin TextBox`). `label` copy verbatim `Label.Caption` của Access.
 */
const COLUMNS: SaleControlColumnDef[] = [
  { key: 'ctrlFilldocTxt', label: 'Filling Docs', filter: 'checkboxes' },
  { key: 'contractSale', label: 'Contract Ref' },
  { key: 'buyersRef', label: "Buyer's Ref" },
  { key: 'contractDate', label: 'Contract Date', kind: 'date' },
  { key: 'cpCode', label: 'Client ID', filter: 'checkboxes' },
  { key: 'clientShort', label: 'Short Name', proper: true },
  { key: 'consignee', label: 'Consignee' , wrap: true, width: 180 },
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right' },
  { key: 'noOfBags', label: 'Bags', kind: 'number', align: 'right' },
  { key: 'packaging', label: 'Package' },
  { key: 'quality', label: 'Quality' },
  { key: 'certification', label: 'Certification', filter: 'checkboxes' },
  { key: 'contractType', label: 'Contract Type', filter: 'checkboxes' },
  { key: 'price', label: 'Price', kind: 'number', align: 'right' },
  { key: 'priceUnit', label: 'Price unit' },
  { key: 'ptbfDiffs', label: 'PTBF Diffs', kind: 'number', align: 'right' },
  { key: 'vatTxt', label: 'Applicable VAT' },
  { key: 'futuresMonth', label: 'Futures Month' },
  { key: 'fixationCall', label: 'Fixation Call' },
  { key: 'fx', label: 'FX', kind: 'number', align: 'right' },
  { key: 'deliveryTerms', label: 'Delivery terms' , wrap: true, width: 160 },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'deliveryFrom', label: 'Shipment From', kind: 'date' },
  { key: 'deliveryTo', label: 'Shipment To', kind: 'date' },
  { key: 'deliveryFrom2', label: 'Delivery From', kind: 'date' },
  { key: 'deliveryTo2', label: 'Delivery To', kind: 'date' },
  { key: 'pssTerms', label: 'PSS terms' , wrap: true, width: 180 },
  { key: 'sampCountAccept', label: 'Sampling - Accept', kind: 'number', align: 'right' },
  { key: 'sampCountReject', label: 'Sampling - Reject', kind: 'number', align: 'right' },
  { key: 'sampCountWaiting', label: 'Sampling - Waiting', kind: 'number', align: 'right' },
  { key: 'paymentWeightTerms', label: 'Payment weight terms' , wrap: true, width: 180 },
  { key: 'franchise', label: 'Franchise', kind: 'percent', align: 'right' },
  { key: 'paymentTerms', label: 'Payment terms' , wrap: true, width: 560 },
  { key: 'conditions', label: 'Conditions' , wrap: true, width: 200 },
  { key: 'avgPrice', label: 'Avg. Price', kind: 'number', align: 'right' },
  { key: 'description', label: 'Description' , wrap: true, width: 220 },
  { key: 'finalizedText', label: 'Finalized?', filter: 'checkboxes' },
  { key: 'finalizedDate', label: 'Finalized Date', kind: 'datetime' },
  { key: 'deliveredBag', label: 'Delivered bag', kind: 'number', align: 'right' },
  { key: 'pendingBag', label: 'Pending bag', kind: 'number', align: 'right' },
  { key: 'delivered', label: 'Delivered Kg', kind: 'number', align: 'right' },
  { key: 'pending', label: 'Pending Kg', kind: 'number', align: 'right' },
  { key: 'tolerance', label: 'Tolerance', kind: 'number', align: 'right' },
  { key: 'dateUpdate', label: 'Date Updated', kind: 'datetime' },
  { key: 'personUpdate', label: 'Person Updated' },
]

/**
 * Cột cộng tổng ở footer — CHỈ 6 cột có `AggregateType=0` (Sum) trong Access
 * `frmCtrlSalesQryLocalS` (`TotalsRow=1`), theo `/convert-table` §10.9. KHÔNG
 * sum mọi cột `kind:'number'` (giá/FX/premium cộng lại vô nghĩa).
 * Access sums: quantity, no_of_bags, pending_bag, delivered, pending, tolerance.
 */
/**
 * Cột freeze khi scroll ngang — skill `/convert-table` §7.6. "Filling Docs" giữ
 * ở vị trí 0 (không freeze) → khi scroll phải sẽ khuất sau Contract Ref.
 * Width fallback theo viewport: mobile hẹp hơn để chừa chỗ scroll.
 */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['contractSale'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = {
  contractSale: 130,
}
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = {
  contractSale: 110,
}

const SUM_KEYS = new Set<string>([
  'quantity',
  'noOfBags',
  'pendingBag',
  'delivered',
  'pending',
  'tolerance',
])

type GridRow = Record<string, unknown> & {
  rowKey: string
  contractSale: string
}

const sumNumericColumn = (rows: GridRow[], key: string): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return ''
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export interface SaleControlLocalSalesViewProps {
  startDate: string
  endDate: string
  isFinalized: SaleControlFinalizedFilter
  invalidRange: boolean
}

export default function SaleControlLocalSalesView({
  startDate,
  endDate,
  isFinalized,
  invalidRange,
}: SaleControlLocalSalesViewProps) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP

  // ----- Header filter từ SaleControlPage truyền xuống — gửi thẳng vào RTK Query
  const queryArgs = useMemo(
    () => ({
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
      ...(invalidRange ? {} : { startDate, endDate }),
      isFinalized,
    }),
    [startDate, endDate, isFinalized, invalidRange],
  )
  const { data, isLoading, isFetching, error, refetch } = useGetCtrlSalesLocalSQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  })

  const baseRows = useMemo<GridRow[]>(() => {
    const items = (data?.items ?? []) as Record<string, unknown>[]
    return items.map((rec, idx) => {
      const contractSale = String(rec.contractSale ?? '')
      const localSId = rec.localSId == null ? '' : String(rec.localSId)
      return {
        ...rec,
        rowKey: `${localSId || idx}-${contractSale}`,
        contractSale,
      }
    })
  }, [data?.items])

  const { getWidth, startResize } = useColumnWidths('sale-control.local-sales')

  // ----- Column state (order/visibility) persist per-user qua user_preferences.
  const columnsCtrl = usePreferenceColumns<SaleControlColumnDef>({
    scope: 'sale_control',
    prefKey: 'columns',
    subKey: 'sales-contract',
    allColumns: COLUMNS,
    widthsTableKey: 'sale-control.local-sales',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  // ----- Filter trên grid (không gọi lại API) — template dataList
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const {
    selectedId: selectedKey,
    toggleSelect: toggleSelectKey,
  } = useRowSelection<string>()

  // Global Search popover (filter bar)
  const filterPopover = useFilterPopover<FilterKind>()
  const closeFilterPopover = () => filterPopover.close()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)

  // Per-column filters (header ▽ icons). Lưu viewport position thay vì DOM element
  // để tránh popover về top-left khi TableVirtuoso remount header.
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )

  // Popover meta (computed khi mở col popover)
  const popoverColumn = useMemo(
    () => (colPopover ? COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, COLUMNS),
    [filteredRows, sortState],
  )

  const rows = useMemo<GridRow[]>(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        COLUMNS
          .filter((col) => SUM_KEYS.has(col.key))
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows],
  )

  const idRangeText = useMemo(() => {
    if (rows.length === 0) return 'ID: —'
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const r of rows) {
      const id = Number(r.localSId)
      if (!Number.isFinite(id)) continue
      if (id < min) min = id
      if (id > max) max = id
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 'ID: —'
    return `ID: ${min}–${max}`
  }, [rows])

  const minTableWidth = Math.max(960, visibleColumns.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: string) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<GridRow | null>(
    () => rows.find((r) => r.rowKey === selectedKey) ?? null,
    [rows, selectedKey],
  )

  // ----- Toolbar actions ----------------------------------------------------------
  const [closeDialogOpen, setCloseDialogOpen] = useState(false)
  const [confirm, setConfirm] = useState<
    | { kind: 'reopen'; contractSale: string }
    | { kind: 'fillingDocs'; contractSale: string; turnOn: boolean }
    | null
  >(null)
  const [reopen, { isLoading: reopenLoading }] = useReopenLocalSMutation()
  const [toggleFillingDocs, { isLoading: fillingLoading }] = useToggleFillingDocsLocalSMutation()

  const handleForcedClose = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    if (isYesText(selectedRow.finalizedText)) {
      snack.warning('This contract is forced close!')
      return
    }
    setCloseDialogOpen(true)
  }

  const handleReopen = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    if (!isYesText(selectedRow.finalizedText)) {
      snack.warning('This contract is not forced close!')
      return
    }
    setConfirm({ kind: 'reopen', contractSale: selectedRow.contractSale })
  }

  const handleFillingDocs = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setConfirm({
      kind: 'fillingDocs',
      contractSale: selectedRow.contractSale,
      turnOn: !isYesText(selectedRow.ctrlFilldocTxt),
    })
  }

  const handleConfirm = async () => {
    if (!confirm) return
    try {
      if (confirm.kind === 'reopen') {
        await reopen({ contractSale: confirm.contractSale }).unwrap()
        snack.success('Successfully Unforced')
      } else {
        await toggleFillingDocs({
          contractSale: confirm.contractSale,
          isCtrlFilldoc: confirm.turnOn,
        }).unwrap()
        snack.success('Successfully Updated')
      }
    } catch (err: unknown) {
      snack.error(getErrorMessage(err, 'Action failed'))
    } finally {
      setConfirm(null)
    }
  }

  // Filling Docs là toggle → đổi label theo state hiện tại của dòng (Y = đã check → Uncheck) — giống Inbound Logistics.
  const fillingChecked = !!selectedRow && isYesText(selectedRow.ctrlFilldocTxt)
  const fillingLabel = fillingChecked ? 'Uncheck Docs' : 'Check Docs'

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Action + filter bar — template dataList */}
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Tooltip
              title={
                !canEdit
                  ? 'Edit permission required for Sales Control.'
                  : !selectedRow
                    ? 'Select a row first.'
                    : isYesText(selectedRow.finalizedText)
                      ? 'Contract is already forced close.'
                      : 'Forced close this contract.'
              }
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  sx={actionToolbarBtnSx}
                  disabled={!canEdit || !selectedRow || isYesText(selectedRow?.finalizedText)}
                  onClick={handleForcedClose}
                >
                  Forced close
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                !canEdit
                  ? 'Admin only.'
                  : !selectedRow
                    ? 'Select a row first.'
                    : !isYesText(selectedRow.finalizedText)
                      ? 'Contract is not forced close.'
                      : 'Re-open a forced-closed contract (admin only).'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  sx={actionToolbarBtnSx}
                  disabled={!canEdit || !selectedRow || !isYesText(selectedRow?.finalizedText)}
                  onClick={handleReopen}
                >
                  Re-open
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                !selectedRow
                  ? 'Select a row first.'
                  : isYesText(selectedRow.ctrlFilldocTxt)
                    ? 'Uncheck completed documents (admin / allowlist).'
                    : 'Check completed documents (admin / allowlist).'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  sx={actionToolbarBtnSx}
                  disabled={!selectedRow}
                  onClick={handleFillingDocs}
                >
                  {fillingLabel}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getErrorMessage(error, 'Failed to load Sales Control rows.')}
        </Alert>
      ) : null}

      <Box className="app-dataframe" sx={{ flex: 1, minHeight: 0, height: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              Loaded: {baseRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
            >
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.68rem' },
                '& .MuiTablePagination-select': { fontSize: '0.68rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.86)', transformOrigin: 'center' },
              }}
            />
            <Button size="small" variant="text" onClick={() => refetch()} sx={filterBarBtnSx}>
              Refresh
            </Button>
          </Stack>
        </Box>

        {/* Global Search popover */}
        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Contract, client, …"
              label="Search rows"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        {/* Per-column filter popover (▽ icon trên header) */}
        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Sales Control rows"
              description="Refresh the page or contact admin if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No rows found"
              description="Try changing the Shipment Period / Is Finalized filter at the top, or the grid filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align ?? (col.kind === 'number' ? 'right' : 'left')}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', fontWeight: 700, ...(isFrozen ? {} : { position: 'relative' }) }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const text = total == null ? '' : formatNumber(total)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align ?? (col.kind === 'number' ? 'right' : 'left')}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : text}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: GridRow) =>
                visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const val = formatCell(row, col)
                  const w = getWidth(col.key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  const wrap = col.wrap || getWidth(col.key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align ?? (col.kind === 'number' ? 'right' : 'left')}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align ?? (col.kind === 'number' ? 'right' : 'left')}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                /* eslint-disable @typescript-eslint/no-explicit-any -- react-virtuoso typing buộc dùng `any` cho TableRow data-item-index hook (đồng bộ pattern `ContractListView.tsx`). */
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  const rowKey = row?.rowKey ?? null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowKey != null && rowKey === selectedKey}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (rowKey != null) toggleSelectKey(rowKey)
                      }}
                    />
                  )
                },
                /* eslint-enable @typescript-eslint/no-explicit-any */
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />

      <SaleControlLocalSCloseDialog
        open={closeDialogOpen}
        contractSale={selectedRow?.contractSale ?? ''}
        pending={(selectedRow?.pending as number | string | null | undefined) ?? null}
        onClose={() => setCloseDialogOpen(false)}
      />

      <ConfirmDialog
        open={confirm?.kind === 'reopen'}
        title="Unforced close"
        message="Do you want to unforce close this contract?"
        confirmLabel="Re-open"
        cancelLabel="Cancel"
        loading={reopenLoading}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />

      <ConfirmDialog
        open={confirm?.kind === 'fillingDocs'}
        title="Filling Docs"
        message={
          confirm?.kind === 'fillingDocs' && confirm.turnOn
            ? 'Do you want to check the completed documents?'
            : 'Do you want to uncheck the completed documents?'
        }
        confirmLabel="OK"
        cancelLabel="Cancel"
        loading={fillingLoading}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </Box>
  )
}
